import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import GroupCard from "../components/GroupCard";
import PendingBanner from "../components/PendingBanner";
import { api } from "../lib/api";
import { queryClient } from "../lib/query";
import { MeOut } from "../lib/schemas";
import { BalanceAmount, Skeleton } from "../ui";

export default function Home() {
  const nav = useNavigate();

  const { data: me, error: meError } = useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeOut>("GET", "/me"),
    refetchInterval: 10_000,
    staleTime: 10_000,
  });

  const totalPending =
    me?.groups.reduce((sum, g) => sum + g.pending_count, 0) ?? 0;

  if (meError && !me) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-danger mb-4">
            {meError instanceof Error ? meError.message : "Failed to load data"}
          </p>
          <button
            type="button"
            onClick={() => void queryClient.invalidateQueries({ queryKey: ["me"] })}
            className="border border-fg px-5 py-2.5 rounded-xl text-fg font-sans text-sm font-semibold active:opacity-70 transition-opacity"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
        <div className="mb-8 text-center">
          <Skeleton className="h-3 w-24 mx-auto mb-3" />
          <Skeleton className="h-12 w-44 mx-auto" />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-surface border border-hairline rounded-2xl shadow-card p-4 mb-2.5 flex items-center gap-3">
            <Skeleton className="w-10 h-10" rounded="rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const groupCurrency = me.groups.length > 0 ? me.groups[0].currency : "USD";
  const allSameCurrency =
    me.groups.length === 0 ||
    me.groups.every((g) => g.currency === groupCurrency);

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
      {/* Hero: total balance */}
      <div className="mb-8 text-center">
        <p className="font-sans text-[13px] font-medium text-muted mb-3">
          Net balance
        </p>
        {allSameCurrency ? (
          <BalanceAmount size="hero" balance={me.total_balance} currency={groupCurrency} />
        ) : (
          <span className="font-sans text-4xl font-semibold text-muted leading-none">Mixed</span>
        )}
      </div>

      {/* Pending banner */}
      <PendingBanner count={totalPending} />

      {/* Group list */}
      <div>
        {me.groups.length === 0 ? (
          <p className="text-muted text-center py-8">No groups yet.</p>
        ) : (
          <>
          <p className="text-xs font-medium text-muted mb-2.5 px-1">Your groups</p>
          {me.groups.map((group, i) => (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.06 }}
            >
              <GroupCard
                group={group}
                onClick={() => nav(`/groups/${group.id}`)}
              />
            </motion.div>
          ))}
          </>
        )}
      </div>
    </div>
  );
}
